<template>
	<div class="change-name-popup-body">
		<div class="change-name-popup-body__header">
			{{ translate("ui.enter_your_username") }}
		</div>
		<div class="change-name-popup-body__input-container">
			<input
				ref="input"
				name="nameChange"
				class="change-name-popup-body__input light-input"
				type="text"
				v-model="nameInputValue"
				minlength="3"
				maxlength="20"
				@focus="errorMessage = ''"
				@keyup.enter="saveNewName"
			/>
			<label class="change-name-popup-body__input__label">
				{{
					translate("ui.character_limit")
						.replace("{num1}", "3")
						.replace("{num2}", "20")
				}}
			</label>
			<div class="change-name-popup-body__input__error">
				{{ errorMessage }}
			</div>
		</div>
		<AppTextButton
			class="change-name-popup-body__btn"
			:type="StatusTypes.PRIMARY"
			:text="translate('button.confirm')"
			:disabled="nameInputValue.length < 3 || nameInputValue.length > 20"
			:shadow="true"
			@click="saveNewName"
		/>
	</div>
</template>

<script lang="ts" setup>
import SoundService from "@/game/SoundService";
import useSocketService from "@/hooks/useSocketService";
import useUserStore from "@/store/useUserStore";
import { StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { markRaw, nextTick, onMounted, ref, useTemplateRef } from "vue";
import AppTextButton from "../AppTextButton.vue";

const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());
const userStore = useUserStore();
const emit = defineEmits(["close"]);
const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

const input = useTemplateRef<HTMLInputElement>("input");
const nameInputValue = ref<string>("");
const errorMessage = ref<string>("");

const saveNewName = (): void => {
	emitGenericClick();
	if (nameInputValue.value.length < 3 || nameInputValue.value.length > 20) {
		errorMessage.value = translate("ui.name_length_check")
			.replace("{num1}", "3")
			.replace("{num2}", "20");
		return;
	}

	// Allow spaces, but not at the start or end, and only Latin letters and numbers
	const latinAlphaNumRegex = /^(?! )[0-9\p{Script=Latin}\p{M} ]*$/u;
	if (!latinAlphaNumRegex.test(nameInputValue.value)) {
		errorMessage.value = translate("ui.name_wrong_char");
		return;
	}

	socketService
		.changeUserName(nameInputValue.value)
		.then((data: string): void => {
			let reason = "invalid_name";
			if (data) {
				reason = data;
				if (reason === "user_name_taken") {
					errorMessage.value = translate("ui.name_taken");
					return;
				}
				if (["invalid_name", "auth_error"].includes(reason)) {
					errorMessage.value = translate("ui.name_wrong_char");
					return;
				}
			}
			userStore.setUserName(nameInputValue.value);
			emit("close");
		});
};

onMounted((): void => {
	if (userStore.nameChanged) {
		nameInputValue.value = userStore.userName;
	}
	nextTick((): void => {
		if (!input.value) return;

		input.value.focus();
		input.value.select();
	});
});
</script>

<style lang="scss" scoped>
.change-name-popup-body {
	@include flex-col-center;
	gap: 0.5rem;
	height: 100%;
	color: #372615;

	&__header {
		margin-bottom: 0.125rem;
		font-style: normal;
		font-weight: normal;
		font-stretch: normal;
		font-size: 0.875rem;
		line-height: normal;
		letter-spacing: normal;
		text-align: center;
	}
	&__input-container {
		@include flex-col-center;
	}
	&__input {
		background-color: rgb(255, 243, 221);
		width: 12.5rem;
		height: 1.813rem;

		&__label {
			width: 100%;
			@include flex-row-end;
			opacity: 0.8;
			font-size: 0.7rem;
		}

		&__error {
			color: rgb(207, 0, 50);
			font-size: 0.7rem;
		}
	}
	&__btn {
		width: 12.5rem;
	}
}
</style>
